import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import UsageLicenseTypeSelect from '~/components/legacy/UsageLicenseTypeSelect.jsx';
import { withRankingPayload } from '~/modules/catalogEventTracking.js';
import { buildProjectLicenseCartRequestData, buildProjectLicenseCartRemoveRequestData } from '~/components/legacy/projectLicenseCartSelection.js';
import legacyEventHub, { cartItemsFromEvent } from '~/modules/legacyRuntime.js';
import { requestJson } from '~/modules/legacyRequest.js';

function formatLength(length) {
  let minutes = Math.floor(length / 60);
  let seconds = length % 60;

  if (minutes < 10) {
    minutes = '0' + minutes;
  }

  if (seconds < 10) {
    seconds = '0' + seconds;
  }

  return minutes + ':' + seconds;
}

class SimilarTracksDrawer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      loading: false,
      error: false,
      loadedFor: null,
      activeTrackId: null,
      playingTrackId: null,
      trackFavorites: this.props.trackFavorites || [],
      cartItems: this.props.cartItems || [],
      selectedTrackForCart: null
    };

    this.createPortalRoot = this.createPortalRoot.bind(this);
    this.destroyPortalRoot = this.destroyPortalRoot.bind(this);
    this.fetchSimilarTracks = this.fetchSimilarTracks.bind(this);
    this.handleTrackPlay = this.handleTrackPlay.bind(this);
    this.handleFavoriteToggle = this.handleFavoriteToggle.bind(this);
    this.handleCartToggle = this.handleCartToggle.bind(this);
    this.closeUsageLicenseTypeSelect = this.closeUsageLicenseTypeSelect.bind(this);
    this.updateProjectLicenseFamily = this.updateProjectLicenseFamily.bind(this);
    this.removeProjectLicenseSelection = this.removeProjectLicenseSelection.bind(this);
    this.trackIsFavorited = this.trackIsFavorited.bind(this);
    this.trackIsInCart = this.trackIsInCart.bind(this);
    this.getSelectedProjectLicenseFamilyKeys = this.getSelectedProjectLicenseFamilyKeys.bind(this);
    this.handleUpdateTrackInfo = this.handleUpdateTrackInfo.bind(this);
    this.handleAudioPlayerPlay = this.handleAudioPlayerPlay.bind(this);
    this.handleAudioPlayerStop = this.handleAudioPlayerStop.bind(this);
    this.handleUpdateTrackFavorites = this.handleUpdateTrackFavorites.bind(this);
    this.handleUpdateCart = this.handleUpdateCart.bind(this);
    this.registerEventHubListener = this.registerEventHubListener.bind(this);
    this.unregisterEventHubListeners = this.unregisterEventHubListeners.bind(this);
    this.renderDrawerMarkup = this.renderDrawerMarkup.bind(this);

    this.eventHubListeners = [];
  }

  componentDidMount() {
    this.createPortalRoot();
    this.registerEventHubListener('updateTrackInfo', this.handleUpdateTrackInfo);
    this.registerEventHubListener('audioPlayerPlay', this.handleAudioPlayerPlay);
    this.registerEventHubListener('audioPlayerStop', this.handleAudioPlayerStop);
    this.registerEventHubListener('audioPlayerEnded', this.handleAudioPlayerStop);
    this.registerEventHubListener('updateTrackFavorites', this.handleUpdateTrackFavorites);
    this.registerEventHubListener('updateCart', this.handleUpdateCart);

    if (this.props.visible) {
      this.fetchSimilarTracks();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.visible && (!prevProps.visible || prevProps.similarTracksUrl !== this.props.similarTracksUrl)) {
      this.fetchSimilarTracks();
    }

    if (prevProps.trackFavorites !== this.props.trackFavorites) {
      this.setState({ trackFavorites: this.props.trackFavorites || [] });
    }

    if (prevProps.cartItems !== this.props.cartItems) {
      this.setState({ cartItems: this.props.cartItems || [] });
    }
  }

  componentWillUnmount() {
    this.unregisterEventHubListeners();
    this.destroyPortalRoot();
  }

  registerEventHubListener(eventName, handler) {
    legacyEventHub.on(eventName, handler);
    this.eventHubListeners.push({ eventName: eventName, handler: handler });
  }

  unregisterEventHubListeners() {
    if (!legacyEventHub || typeof legacyEventHub.off !== 'function') {
      this.eventHubListeners = [];
      return;
    }

    this.eventHubListeners.forEach((listener) => {
      legacyEventHub.off(listener.eventName, listener.handler);
    });
    this.eventHubListeners = [];
  }

  createPortalRoot() {
    if (typeof document === 'undefined' || this.portalRoot) {
      return;
    }

    this.portalRoot = document.createElement('div');
    this.portalRoot.className = 'similar-tracks-drawer__portal';
    document.body.appendChild(this.portalRoot);
  }

  destroyPortalRoot() {
    if (!this.portalRoot) {
      return;
    }

    if (this.portalRoot.parentNode) {
      this.portalRoot.parentNode.removeChild(this.portalRoot);
    }

    this.portalRoot = null;
  }

  fetchSimilarTracks() {
    const similarTracksUrl = this.props.similarTracksUrl;

    if (!similarTracksUrl || this.state.loading || this.state.loadedFor === similarTracksUrl) {
      return;
    }

    this.setState({
      loading: true,
      error: false
    });

    return fetch(similarTracksUrl, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('similar_tracks_request_failed');
        }

        return response.json();
      })
      .then((data) => {
        this.setState({
          items: data.items || [],
          loading: false,
          error: false,
          loadedFor: similarTracksUrl
        });
      })
      .catch(() => {
        this.setState({
          items: [],
          loading: false,
          error: true,
          loadedFor: null
        });
      });
  }

  handleUpdateTrackInfo(data) {
    const nextTrack = Array.isArray(data) ? data[0] : data;
    if (!nextTrack || !nextTrack.id) return;

    this.setState({ activeTrackId: nextTrack.id });
  }

  handleAudioPlayerPlay(data) {
    const nextTrack = Array.isArray(data) ? data[0] : data;
    if (!nextTrack || !nextTrack.id) return;

    this.setState({
      activeTrackId: nextTrack.id,
      playingTrackId: nextTrack.id
    });
  }

  handleAudioPlayerStop() {
    this.setState({ playingTrackId: null });
  }

  handleUpdateTrackFavorites(data) {
    this.setState({ trackFavorites: Array.isArray(data?.[0]) ? data[0] : [] });
  }

  handleUpdateCart(data) {
    this.setState({ cartItems: cartItemsFromEvent(data) });
  }

  handleTrackPlay(track, event) {
    event.preventDefault();
    const isCurrentTrack = String(this.state.activeTrackId) === String(track.id);
    const isPlaying = String(this.state.playingTrackId) === String(track.id);
    const trackInfoPayload = {
      id: track.id,
      title: track.title,
      display_title: track.display_title,
      composerName: track.composer_name,
      showcaseUrl: track.showcase_url,
      track_image_markup: track.track_image,
      length: track.length_seconds,
      track_variants: track.track_variants || [],
      royaltyFree: track.royalty_free,
      pro_short_name: track.pro_short_name,
      youtube_content_id_enabled: track.youtube_content_id_enabled
    };

    legacyEventHub.trigger('playTrack', track.id, trackInfoPayload);
    legacyEventHub.trigger('updateTrackInfo', trackInfoPayload);

    this.setState({
      activeTrackId: track.id,
      playingTrackId: isCurrentTrack && isPlaying ? null : track.id
    });
  }

  handleFavoriteToggle(track, event) {
    event.preventDefault();

    return requestJson("/track_favorite?track_id=" + track.id, {
      method: 'POST',
      data: {}
    }).then((data) => {
      if (!this.trackIsFavorited(track.id)) {
        legacyEventHub.trigger('trackFavorited', track.id);
      }

      legacyEventHub.trigger('updateTrackFavorites', data);
    });
  }

  handleCartToggle(track, event) {
    event.preventDefault();
    this.setState({ selectedTrackForCart: track });
  }

  closeUsageLicenseTypeSelect(event) {
    if (event) {
      event.preventDefault();
    }

    this.setState({ selectedTrackForCart: null });
  }

  updateProjectLicenseFamily(projectLicenseSelection) {
    const track = this.state.selectedTrackForCart;
    const requestData = track ? buildProjectLicenseCartRequestData(track.id, projectLicenseSelection, withRankingPayload({ location: window.location.href }, track)) : null;
    if (!track || !requestData) return null;

    return requestJson('/cart', {
      method: 'POST',
      data: requestData
    }).then((data) => {
      legacyEventHub.trigger('updateCart', data);
      this.setState({ selectedTrackForCart: null });
    });
  }

  removeProjectLicenseSelection(projectLicenseSelection) {
    const track = this.state.selectedTrackForCart;
    const requestData = track ? buildProjectLicenseCartRemoveRequestData(track.id, projectLicenseSelection) : null;
    if (!track || !requestData) return null;

    return requestJson('/cart', {
      method: 'POST',
      data: requestData
    }).then((data) => {
      legacyEventHub.trigger('updateCart', data);
      this.setState({ selectedTrackForCart: null });
    });
  }

  trackIsFavorited(trackId) {
    return (this.state.trackFavorites || []).some((favorite) => String(favorite.track_id) === String(trackId));
  }

  trackIsInCart(trackId) {
    return (this.state.cartItems || []).some((item) => String(item.track_id) === String(trackId));
  }

  getSelectedProjectLicenseFamilyKeys(trackId) {
    return (this.state.cartItems || [])
      .filter((item) => String(item.track_id) === String(trackId) && item.project_license_family_key)
      .map((item) => item.project_license_family_key);
  }

  getSelectedProjectLicenseSelections(trackId) {
    return (this.state.cartItems || [])
      .filter((item) => String(item.track_id) === String(trackId) && item.project_license_family_key)
      .map((item) => ({
        familyKey: item.project_license_family_key,
        usageLicenseTypeId: item.usage_license_type_id,
        selectedOptionKeys: item.project_license_option_keys,
        customerProjectLicensePresetId: item.customer_project_license_preset_id,
        customerEventLicensePresetId: item.customer_event_license_preset_id,
        projectLicensePresetName: item.project_license_preset_name
      }));
  }

  renderDrawerMarkup() {
    if (!this.props.visible) {
      return null;
    }

    const closeLabel = this.props.locale === 'de' ? 'Ähnliche Titel schließen' : 'Close similar tracks';
    const heading = this.props.locale === 'de' ? 'Ähnliche Titel' : 'Similar tracks';
    const loadingLabel = this.props.locale === 'de' ? 'Ähnliche Titel werden geladen …' : 'Loading similar tracks …';
    const emptyLabel = this.props.locale === 'de' ? 'Keine ähnlichen Titel gefunden.' : 'No similar tracks found.';
    const errorLabel = this.props.locale === 'de' ? 'Ähnliche Titel konnten nicht geladen werden.' : 'Could not load similar tracks.';
    const playLabel = this.props.locale === 'de' ? 'Track abspielen' : 'Play track';
    const pauseLabel = this.props.locale === 'de' ? 'Track pausieren' : 'Pause track';
    const detailsLabel = this.props.locale === 'de' ? 'Zum Track' : 'View track';
    const favoriteLabel = this.props.locale === 'de' ? 'Merken' : 'Remember';
    const rememberedLabel = this.props.locale === 'de' ? 'Gemerkt' : 'Remembered';
    const licenseLabel = this.props.locale === 'de' ? 'Lizenz' : 'License';
    const referenceHeading = this.props.locale === 'de' ? 'Ausgangspunkt' : 'Reference track';
    const listHeading = this.props.locale === 'de' ? 'Schnell durchhören' : 'Quick listen';

    let content;

    if (this.state.loading) {
      content = (
        <div className='similar-tracks-drawer__state similar-tracks-drawer__state--loading' aria-live='polite'>
          <div className='similar-tracks-drawer__spinner' aria-hidden='true'>
            <div className='loading-bars'>
              <span className='loading-bars__element'></span>
              <span className='loading-bars__element'></span>
              <span className='loading-bars__element'></span>
              <span className='loading-bars__element'></span>
              <span className='loading-bars__element'></span>
              <span className='loading-bars__element'></span>
              <span className='loading-bars__element'></span>
              <span className='loading-bars__element'></span>
              <span className='loading-bars__element'></span>
              <span className='loading-bars__element'></span>
              <span className='loading-bars__element'></span>
              <span className='loading-bars__element'></span>
              <span className='loading-bars__element'></span>
              <span className='loading-bars__element'></span>
              <span className='loading-bars__element'></span>
            </div>
          </div>
          <div>{loadingLabel}</div>
        </div>
      );
    } else if (this.state.error) {
      content = <div className='similar-tracks-drawer__state similar-tracks-drawer__state--error'>{errorLabel}</div>;
    } else if (this.state.items.length === 0) {
      content = <div className='similar-tracks-drawer__state'>{emptyLabel}</div>;
    } else {
      content = (
        <div className='similar-tracks-drawer__list'>
          {this.state.items.map((item) => {
            const track = item.track || {};
            const isPlaying = String(this.state.playingTrackId) === String(track.id);
            const isFavorited = this.trackIsFavorited(track.id);
            const isInCart = this.trackIsInCart(track.id);

            return (
              <div className='similar-tracks-drawer__item' key={'similar-track-' + item.id}>
                <div className='similar-tracks-drawer__item-media'>
                  {track.track_image ? (
                    <div
                      className='similar-tracks-drawer__item-image'
                      dangerouslySetInnerHTML={{ __html: track.track_image }}>
                    </div>
                  ) : null}
                  <button
                    type='button'
                    className='similar-tracks-drawer__item-action similar-tracks-drawer__item-action--cover'
                    onClick={(event) => this.handleTrackPlay(track, event)}
                    aria-label={isPlaying ? pauseLabel : playLabel}>
                    <i className={isPlaying ? 'fa fa-pause' : 'fa fa-play'}></i>
                  </button>
                </div>
                <div className='similar-tracks-drawer__item-main'>
                  <div className='similar-tracks-drawer__item-title-row'>
                    <a
                      href={track.showcase_url}
                      target='_blank'
                      rel='noreferrer'
                      className='similar-tracks-drawer__item-title'>
                      {track.display_title || track.title}
                    </a>
                    <span className='similar-tracks-drawer__item-length'>{formatLength(track.length_seconds || 0)}</span>
                  </div>
                  {track.composer_name ? (
                    <div className='similar-tracks-drawer__item-artist'>{track.composer_name}</div>
                  ) : null}
                  <div className='similar-tracks-drawer__item-actions'>
                    <button
                      type='button'
                      className='similar-tracks-drawer__item-action similar-tracks-drawer__item-action--ghost'
                      onClick={(event) => this.handleFavoriteToggle(track, event)}
                      aria-label={isFavorited ? rememberedLabel : favoriteLabel}>
                      <i className={isFavorited ? 'fa fa-heart' : 'fa fa-heart-o'}></i>
                    </button>
                    <button
                      type='button'
                      className='similar-tracks-drawer__item-action similar-tracks-drawer__item-action--ghost'
                      onClick={(event) => this.handleCartToggle(track, event)}
                      aria-label={licenseLabel}>
                      <i className={isInCart ? 'fa fa-check' : 'fa fa-shopping-cart'}></i>
                    </button>
                    <a
                      href={track.showcase_url}
                      target='_blank'
                      rel='noreferrer'
                      className='similar-tracks-drawer__item-action similar-tracks-drawer__item-action--ghost'
                      aria-label={detailsLabel}>
                      <i className='fa fa-arrow-right'></i>
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className='similar-tracks-drawer'>
        <div className='similar-tracks-drawer__backdrop' onClick={this.props.onRequestClose}></div>
        <div className='similar-tracks-drawer__panel'>
          <div className='similar-tracks-drawer__header'>
            <div className='similar-tracks-drawer__heading'>{heading}</div>
            <button type='button' className='similar-tracks-drawer__close' onClick={this.props.onRequestClose} aria-label={closeLabel}>
              <i className='fa fa-times'></i>
            </button>
          </div>

          <div className='similar-tracks-drawer__body'>
            <div className='similar-tracks-drawer__section-title'>{referenceHeading}</div>
            <div className='similar-tracks-drawer__reference'>
              {this.props.trackImageMarkup ? (
                <div className='similar-tracks-drawer__reference-image' dangerouslySetInnerHTML={{ __html: this.props.trackImageMarkup }}></div>
              ) : null}
              <div className='similar-tracks-drawer__reference-main'>
                <div className='similar-tracks-drawer__reference-title-row'>
                  <span className='similar-tracks-drawer__reference-title'>{this.props.trackTitle}</span>
                  <span className='similar-tracks-drawer__reference-length'>{formatLength(this.props.length)}</span>
                </div>
                {this.props.trackArtist ? (
                  <div className='similar-tracks-drawer__reference-artist'>{this.props.trackArtist}</div>
                ) : null}
              </div>
            </div>

            <div className='similar-tracks-drawer__section-title'>{listHeading}</div>
            {content}
          </div>
        </div>
        {this.state.selectedTrackForCart ? (
          <UsageLicenseTypeSelect
            locale={this.props.locale}
            addToCart={this.props.locale === 'de' ? 'zum Warenkorb hinzufügen' : 'add to cart'}
            discount={false}
            free={false}
            subscribedUsageLicenseTypes={this.props.subscribedUsageLicenseTypes}
            usageLicenseTypes={this.props.usageLicenseTypes}
            updateProjectLicenseFamily={this.updateProjectLicenseFamily}
            removeProjectLicenseSelection={this.removeProjectLicenseSelection}
            licenseNavigatorPath={this.props.licenseNavigatorPath}
            selectionMode='project_license_family'
            i18n={this.props.i18n}
            visible={true}
            selectedUsageLicenseTypeIds={[]}
            selectedProjectLicenseFamilyKeys={this.getSelectedProjectLicenseFamilyKeys(this.state.selectedTrackForCart.id)}
            selectedProjectLicenseSelections={this.getSelectedProjectLicenseSelections(this.state.selectedTrackForCart.id)}
            cartItems={this.props.cartItems}
            position='overlay'
            show_download_button={false}
            handleCartClick={this.closeUsageLicenseTypeSelect}
            onRequestClose={this.closeUsageLicenseTypeSelect}
            trackTitle={this.state.selectedTrackForCart.display_title || this.state.selectedTrackForCart.title}
            trackArtist={this.state.selectedTrackForCart.composer_name}
            trackImageMarkup={this.state.selectedTrackForCart.track_image} />
        ) : null}
      </div>
    );
  }

  render() {
    const drawerMarkup = this.renderDrawerMarkup();

    if (!drawerMarkup) {
      return null;
    }

    if (this.portalRoot) {
      return ReactDOM.createPortal(drawerMarkup, this.portalRoot);
    }

    return drawerMarkup;
  }
}

SimilarTracksDrawer.defaultProps = {
  locale: 'de',
  length: 0,
  similarTracksUrl: null,
  cartItems: [],
  i18n: {},
  licenseNavigatorPath: null,
  trackArtist: '',
  trackFavorites: [],
  trackImageMarkup: null,
  trackTitle: '',
  subscribedUsageLicenseTypes: [],
  usageLicenseTypes: []
};

export default SimilarTracksDrawer;
